import { useState, useEffect } from 'react';
import { Users, UsersRound, TrendingUp, Monitor, ChevronDown, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';

// ─── Mobile detection ─────────────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [is, setIs] = useState(window.innerWidth < bp);
  useEffect(() => {
    const h = () => setIs(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return is;
}

// ─── Shared fallthrough notice ────────────────────────────────────────────────
function FallthroughNotice({ layer }) {
  const { t } = useLocale();
  return layer.fallsTo ? (
    <div style={{
      padding: '10px 12px',
      backgroundColor: 'color-mix(in srgb, var(--accent-warning) 8%, var(--bg-tertiary))',
      border: '1px solid color-mix(in srgb, var(--accent-warning) 20%, var(--border-color))',
      borderRadius: '10px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5',
    }}>
      💡 <strong style={{ color: 'var(--text-primary)' }}>{t('cascade.leaveFieldEmpty')}</strong>{' '}
      {t('cascade.skipLayer')} <strong style={{ color: 'var(--text-primary)' }}>{layer.fallsTo}</strong>.
    </div>
  ) : (
    <div style={{
      padding: '10px 12px',
      backgroundColor: 'color-mix(in srgb, var(--accent-danger) 8%, var(--bg-tertiary))',
      border: '1px solid color-mix(in srgb, var(--accent-danger) 20%, var(--border-color))',
      borderRadius: '10px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5',
    }}>
      🛑 <strong style={{ color: 'var(--text-primary)' }}>{t('cascade.finalFallback')}</strong>{' '}
      {t('cascade.hardLimitsNote')} <strong style={{ color: 'var(--accent-danger)' }}>CAP</strong> {t('cascade.hardLimitsNote2')}
    </div>
  );
}

// ─── Field list ───────────────────────────────────────────────────────────────
function FieldList({ layer, grid = false }) {
  return (
    <div style={{ display: grid ? 'grid' : 'block', gridTemplateColumns: grid ? '1fr 1fr' : undefined, gap: grid ? '6px' : undefined }}>
      {layer.fields.map((f, i) => (
        <div key={i}
          style={{
            display: 'flex', alignItems: 'flex-start', gap: '8px',
            padding: grid ? '8px 10px' : '7px 8px',
            borderRadius: '8px',
            backgroundColor: grid ? 'var(--bg-tertiary)' : 'transparent',
            border: grid ? `1px solid ${f.isCap ? 'color-mix(in srgb, var(--accent-danger) 20%, var(--border-color))' : 'var(--border-color)'}` : 'none',
            transition: grid ? 'none' : 'background 0.15s',
          }}
          onMouseEnter={e => { if (!grid) e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'; }}
          onMouseLeave={e => { if (!grid) e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <div style={{
            width: '6px', height: '6px', borderRadius: '50%', marginTop: '4px', flexShrink: 0,
            backgroundColor: f.isCap ? '#ef4444' : layer.colorHex,
          }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>{f.name}</span>
              {f.isCap && (
                <span style={{
                  fontSize: '8px', fontWeight: '800', padding: '1px 5px', borderRadius: '4px',
                  backgroundColor: 'color-mix(in srgb, var(--accent-danger) 15%, transparent)',
                  color: 'var(--accent-danger)',
                  border: '1px solid color-mix(in srgb, var(--accent-danger) 25%, transparent)',
                  letterSpacing: '0.05em',
                }}>CAP</span>
              )}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '1px', lineHeight: '1.4' }}>{f.desc}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Desktop layer card ───────────────────────────────────────────────────────
function DesktopLayerCard({ layer, isActive, onClick }) {
  const Icon = layer.icon;
  return (
    <div
      onClick={onClick}
      style={{
        width: layer.width, margin: '0 auto', cursor: 'pointer', borderRadius: '12px', padding: '13px 16px',
        border: `1px solid ${isActive ? `color-mix(in srgb, ${layer.colorHex} 35%, var(--border-color))` : 'var(--border-color)'}`,
        backgroundColor: isActive ? `color-mix(in srgb, ${layer.colorHex} 7%, var(--bg-secondary))` : 'var(--bg-secondary)',
        boxShadow: isActive ? `0 0 16px color-mix(in srgb, ${layer.colorHex} 12%, transparent)` : 'none',
        display: 'flex', alignItems: 'center', gap: '12px',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'; }}
      onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'; }}
    >
      <div style={{
        width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
        backgroundColor: isActive ? layer.colorHex : `color-mix(in srgb, ${layer.colorHex} 12%, var(--bg-tertiary))`,
        color: isActive ? '#fff' : layer.colorHex,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '12px', fontWeight: '800',
        border: `2px solid ${isActive ? layer.colorHex : `color-mix(in srgb, ${layer.colorHex} 25%, transparent)`}`,
        transition: 'all 0.2s',
      }}>P{layer.priority}</div>

      <Icon style={{ width: '16px', height: '16px', flexShrink: 0, color: isActive ? layer.colorHex : 'var(--text-tertiary)', transition: 'color 0.2s' }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)', transition: 'color 0.2s' }}>
            {layer.label}
          </span>
          <span style={{
            fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '20px',
            backgroundColor: `color-mix(in srgb, ${layer.colorHex} 10%, var(--bg-tertiary))`,
            color: layer.colorHex,
            border: `1px solid color-mix(in srgb, ${layer.colorHex} 20%, var(--border-color))`,
            letterSpacing: '0.04em',
          }}>{layer.who.toUpperCase()}</span>
        </div>
        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '1px' }}>{layer.sublabel}</div>
      </div>

      <ChevronDown style={{
        width: '13px', height: '13px', flexShrink: 0,
        color: isActive ? layer.colorHex : 'var(--text-tertiary)',
        transform: isActive ? 'rotate(-90deg)' : 'rotate(0deg)',
        transition: 'all 0.2s',
      }} />
    </div>
  );
}

// ─── Detail panel (desktop right column) ─────────────────────────────────────
function DetailPanel({ layer, navigate }) {
  const { t } = useLocale();
  const Icon = layer.icon;
  return (
    <div style={{
      border: `1px solid color-mix(in srgb, ${layer.colorHex} 25%, var(--border-color))`,
      borderRadius: '14px', overflow: 'hidden',
      backgroundColor: 'var(--bg-secondary)',
      boxShadow: `0 0 24px color-mix(in srgb, ${layer.colorHex} 8%, transparent)`,
      animation: 'panelIn 0.18s ease',
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 18px',
        borderBottom: `1px solid color-mix(in srgb, ${layer.colorHex} 15%, var(--border-color))`,
        backgroundColor: `color-mix(in srgb, ${layer.colorHex} 5%, var(--bg-secondary))`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <Icon style={{ width: '17px', height: '17px', color: layer.colorHex }} />
          <div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>{layer.label}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
              {t('cascade.priority')} {layer.priority} · {layer.who}
            </div>
          </div>
        </div>
        {layer.path && (
          <button onClick={() => navigate(layer.path)} style={{
            display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: '600',
            padding: '5px 9px', borderRadius: '8px', cursor: 'pointer',
            backgroundColor: `color-mix(in srgb, ${layer.colorHex} 10%, var(--bg-tertiary))`,
            color: layer.colorHex,
            border: `1px solid color-mix(in srgb, ${layer.colorHex} 20%, var(--border-color))`,
          }}>
            <ExternalLink style={{ width: '11px', height: '11px' }} />{layer.pathLabel}
          </button>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <FallthroughNotice layer={layer} />
        <FieldList layer={layer} />
      </div>
    </div>
  );
}

// ─── Mobile accordion card ────────────────────────────────────────────────────
function MobileLayerCard({ layer, navigate }) {
  const { t } = useLocale();
  const [open, setOpen] = useState(layer.priority === 1);
  const Icon = layer.icon;

  return (
    <div style={{
      borderRadius: '14px', overflow: 'hidden',
      border: `1px solid ${open ? `color-mix(in srgb, ${layer.colorHex} 30%, var(--border-color))` : 'var(--border-color)'}`,
      backgroundColor: 'var(--bg-secondary)',
      transition: 'border-color 0.2s, box-shadow 0.2s',
      boxShadow: open ? `0 0 14px color-mix(in srgb, ${layer.colorHex} 8%, transparent)` : 'none',
    }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: '11px',
        padding: '13px 15px', textAlign: 'left',
        backgroundColor: open ? `color-mix(in srgb, ${layer.colorHex} 5%, var(--bg-secondary))` : 'transparent',
        borderBottom: open ? `1px solid color-mix(in srgb, ${layer.colorHex} 12%, var(--border-color))` : 'none',
      }}>
        <div style={{
          width: '30px', height: '30px', borderRadius: '50%', flexShrink: 0,
          backgroundColor: open ? layer.colorHex : `color-mix(in srgb, ${layer.colorHex} 12%, var(--bg-tertiary))`,
          color: open ? '#fff' : layer.colorHex,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '11px', fontWeight: '800',
          border: `2px solid ${open ? layer.colorHex : `color-mix(in srgb, ${layer.colorHex} 25%, transparent)`}`,
        }}>P{layer.priority}</div>
        <Icon style={{ width: '15px', height: '15px', color: open ? layer.colorHex : 'var(--text-tertiary)', flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>{layer.label}</div>
          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>{layer.sublabel}</div>
        </div>
        <ChevronDown style={{
          width: '15px', height: '15px', flexShrink: 0, color: 'var(--text-tertiary)',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s',
        }} />
      </button>

      {open && (
        <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', fontSize: '10px', fontWeight: '700',
            padding: '3px 8px', borderRadius: '20px',
            backgroundColor: `color-mix(in srgb, ${layer.colorHex} 10%, var(--bg-tertiary))`,
            color: layer.colorHex,
            border: `1px solid color-mix(in srgb, ${layer.colorHex} 20%, var(--border-color))`,
          }}>{layer.who.toUpperCase()}</div>

          <FallthroughNotice layer={layer} />
          <FieldList layer={layer} grid />

          {layer.path && (
            <button onClick={() => navigate(layer.path)} style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              padding: '10px', borderRadius: '10px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
              backgroundColor: `color-mix(in srgb, ${layer.colorHex} 10%, var(--bg-tertiary))`,
              color: layer.colorHex,
              border: `1px solid color-mix(in srgb, ${layer.colorHex} 20%, var(--border-color))`,
            }}>
              <ExternalLink style={{ width: '13px', height: '13px' }} />{layer.pathLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function CascadePage() {
  const { t } = useLocale();
  const [activeId, setActiveId] = useState('user');
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  // ── Layers defined inside component so t() is available ───────────────────
  const layers = [
    {
      id: 'user',
      priority: 1,
      label: t('cascade.layers.userOverride'),
      sublabel: t('cascade.layers.userSublabel'),
      who: t('cascade.setByBroker'),
      colorHex: '#ef4444',
      icon: Users,
      path: '/clients',
      pathLabel: t('cascade.goToClients'),
      width: '100%',
      tooltip: t('cascade.layers.userTooltip'),
      fields: [
        { name: t('cascade.fields.maxLeverage'),      desc: t('cascade.fieldDescs.capLeverageClient') },
        { name: t('cascade.fields.maxLotSize'),       desc: t('cascade.fieldDescs.limitPositionSize') },
        { name: t('cascade.fields.marginCallLevel'),  desc: t('cascade.fieldDescs.overrideAlertThreshold') },
        { name: t('cascade.fields.stopOutLevel'),     desc: t('cascade.fieldDescs.overrideAutoClose') },
        { name: t('cascade.fields.maxOpenPositions'), desc: t('cascade.fieldDescs.limitConcurrentTrades') },
        { name: t('cascade.fields.commissionPerLot'), desc: t('cascade.fieldDescs.customCommission') },
        { name: t('cascade.fields.tradingDisabled'),  desc: t('cascade.fieldDescs.killSwitch') },
      ],
      fallsTo: t('cascade.layers.userFallsTo'),
    },
    {
      id: 'group',
      priority: 2,
      label: t('cascade.layers.accountGroup'),
      sublabel: t('cascade.layers.groupSublabel'),
      who: t('cascade.setByBroker'),
      colorHex: '#f97316',
      icon: UsersRound,
      path: '/account-groups',
      pathLabel: t('cascade.goToAccountGroups'),
      width: '82%',
      tooltip: t('cascade.layers.groupTooltip'),
      fields: [
        { name: t('cascade.fields.commissionPerLot'), desc: t('cascade.fieldDescs.groupCommission') },
        { name: t('cascade.fields.maxLeverage'),      desc: t('cascade.fieldDescs.groupLeverage') },
        { name: t('cascade.fields.marginCallLevel'),  desc: t('cascade.fieldDescs.groupMarginCall') },
        { name: t('cascade.fields.stopOutLevel'),     desc: t('cascade.fieldDescs.groupStopOut') },
        { name: t('cascade.fields.maxOpenPositions'), desc: t('cascade.fieldDescs.groupPositions') },
        { name: t('cascade.fields.maxLotSize'),       desc: t('cascade.fieldDescs.groupLotSize') },
      ],
      fallsTo: t('cascade.layers.groupFallsTo'),
    },
    {
      id: 'symbol',
      priority: 3,
      label: t('cascade.layers.symbolSettings'),
      sublabel: t('cascade.layers.symbolSublabel'),
      who: t('cascade.setByBroker'),
      colorHex: '#3b82f6',
      icon: TrendingUp,
      path: '/instruments',
      pathLabel: t('cascade.goToInstruments'),
      width: '64%',
      tooltip: t('cascade.layers.symbolTooltip'),
      fields: [
        { name: t('cascade.fields.spreadMarkup'),     desc: t('cascade.fieldDescs.pipsAddedSpread') },
        { name: t('cascade.fields.commissionPerLot'), desc: t('cascade.fieldDescs.symbolCommission') },
        { name: t('cascade.fields.swapMarkup'),       desc: t('cascade.fieldDescs.overnightSwap') },
        { name: t('cascade.fields.tripleSwapDay'),    desc: t('cascade.fieldDescs.tripleSwapDay') },
        { name: t('cascade.fields.maxLeverage'),      desc: t('cascade.fieldDescs.symbolLeverage') },
        { name: t('cascade.fields.maxLotSize'),       desc: t('cascade.fieldDescs.symbolLotSize') },
        { name: t('cascade.fields.stopLevel'),        desc: t('cascade.fieldDescs.minSlTpDistance') },
        { name: t('cascade.fields.freezeLevel'),      desc: t('cascade.fieldDescs.modificationFreeze') },
      ],
      fallsTo: t('cascade.layers.symbolFallsTo'),
    },
    {
      id: 'platform',
      priority: 4,
      label: t('cascade.layers.platformDefaults'),
      sublabel: t('cascade.layers.platformSublabel'),
      who: t('cascade.setBySystemAdmin'),
      colorHex: '#8b5cf6',
      icon: Monitor,
      path: null,
      pathLabel: null,
      width: '46%',
      tooltip: t('cascade.layers.platformTooltip'),
      fields: [
        { name: t('cascade.fields.defaultLeverage'),   desc: t('cascade.fieldDescs.fallbackLeverage') },
        { name: t('cascade.fields.defaultCommission'),  desc: t('cascade.fieldDescs.fallbackCommission') },
        { name: t('cascade.fields.defaultMarginCall'),  desc: t('cascade.fieldDescs.fallbackMarginCall') },
        { name: t('cascade.fields.defaultStopOut'),     desc: t('cascade.fieldDescs.fallbackStopOut') },
        { name: t('cascade.fields.maxLeverageCap'),     desc: t('cascade.fieldDescs.hardCeilingLeverage'),   isCap: true },
        { name: t('cascade.fields.minMarginCall'),      desc: t('cascade.fieldDescs.hardFloorMarginCall'),   isCap: true },
        { name: t('cascade.fields.minStopOut'),         desc: t('cascade.fieldDescs.hardFloorStopOut'),      isCap: true },
        { name: t('cascade.fields.maxCommissionCap'),   desc: t('cascade.fieldDescs.hardCeilingCommission'), isCap: true },
        { name: t('cascade.fields.maxPositionsCap'),    desc: t('cascade.fieldDescs.systemPositionLimit'),   isCap: true },
      ],
      fallsTo: null,
    },
  ];

  const activeLayer = layers.find(l => l.id === activeId);

  // ── MOBILE ────────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ backgroundColor: 'var(--bg-primary)', minHeight: '100vh' }} className="-mx-4 -my-6">
        <style>{`@keyframes panelIn { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }`}</style>

        {/* Hero */}
        <div style={{
          padding: '20px 16px 24px',
          background: 'linear-gradient(135deg, var(--accent-primary) 0%, color-mix(in srgb, var(--accent-primary) 60%, var(--accent-info)) 100%)',
        }}>
          <p style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.6)', marginBottom: '4px' }}>
            {t('cascade.settingsArchitecture')}
          </p>
          <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#fff', margin: '0 0 8px', letterSpacing: '-0.02em' }}>
            {t('cascade.title')}
          </h1>
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', lineHeight: '1.5', margin: 0 }}>
            {t('cascade.subtitle')}{' '}
            <strong style={{ color: '#fbbf24' }}>{t('cascade.subtitleNull')}</strong>{' '}
            {t('cascade.subtitleFallthrough')}
          </p>
        </div>

        {/* Flow pills */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '5px', padding: '10px 14px',
          overflowX: 'auto', borderBottom: '1px solid var(--border-color)',
          backgroundColor: 'var(--bg-secondary)',
        }}>
          {layers.map((l, i) => (
            <>
              <span key={l.id} style={{
                fontSize: '10px', fontWeight: '800', padding: '3px 7px', borderRadius: '20px', flexShrink: 0,
                backgroundColor: `color-mix(in srgb, ${l.colorHex} 10%, var(--bg-tertiary))`,
                color: l.colorHex,
                border: `1px solid color-mix(in srgb, ${l.colorHex} 20%, var(--border-color))`,
              }}>P{l.priority} {l.label}</span>
              {i < layers.length - 1 && (
                <span key={`a${i}`} style={{ fontSize: '11px', color: 'var(--text-tertiary)', flexShrink: 0 }}>→</span>
              )}
            </>
          ))}
        </div>

        {/* Accordion */}
        <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {layers.map((layer, idx) => (
            <div key={layer.id}>
              <MobileLayerCard layer={layer} navigate={navigate} />
              {idx < layers.length - 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '3px 8px' }}>
                  <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }} />
                  <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontWeight: '600', whiteSpace: 'nowrap' }}>
                    {t('cascade.ifEmptyFallsThrough')}
                  </span>
                  <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── DESKTOP ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      <style>{`@keyframes panelIn { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }`}</style>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold mb-2">{t('cascade.title')}</h1>
        <p style={{ color: 'var(--text-secondary)' }}>
          {t('cascade.subtitle')}{' '}
          <strong style={{ color: 'var(--accent-warning)' }}>{t('cascade.subtitleNull')}</strong>{' '}
          {t('cascade.subtitleFallthrough')}
        </p>
      </div>

      {/* Resolution flow bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap',
        padding: '11px 14px', borderRadius: '12px',
        backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
      }}>
        <span style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-tertiary)', letterSpacing: '0.08em', marginRight: '2px' }}>
          {t('cascade.resolutionOrder')}
        </span>
        {layers.map((l, i) => (
          <>
            <button key={l.id} onClick={() => setActiveId(l.id)} style={{
              fontSize: '11px', fontWeight: '700', padding: '4px 10px', borderRadius: '20px', cursor: 'pointer',
              backgroundColor: activeId === l.id ? l.colorHex : `color-mix(in srgb, ${l.colorHex} 10%, var(--bg-tertiary))`,
              color: activeId === l.id ? '#fff' : l.colorHex,
              border: `1px solid color-mix(in srgb, ${l.colorHex} 25%, var(--border-color))`,
              transition: 'all 0.15s ease',
            }}>P{l.priority} · {l.label}</button>
            {i < layers.length - 1 && (
              <span key={`a${i}`} style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>→</span>
            )}
          </>
        ))}
        <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--text-tertiary)' }}>
          {t('cascade.firstNonNull')}
        </span>
      </div>

      {/* Main grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'start' }}>

        {/* Left: pyramid */}
        <div>
          <p style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-tertiary)', letterSpacing: '0.08em', marginBottom: '10px' }}>
            {t('cascade.highestPriority')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {layers.map((layer, idx) => (
              <div key={layer.id}>
                <DesktopLayerCard layer={layer} isActive={activeId === layer.id} onClick={() => setActiveId(layer.id)} />
                {idx < layers.length - 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 8px' }}>
                    <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }} />
                    <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', fontWeight: '600', whiteSpace: 'nowrap' }}>
                      {t('cascade.ifEmptyFallsThrough')}
                    </span>
                    <div style={{ flex: 1, height: '1px', backgroundColor: 'var(--border-color)' }} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Legend */}
          <div style={{ marginTop: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {layers.map(l => (
              <div key={l.id} style={{
                padding: '10px 12px', borderRadius: '10px',
                backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: l.colorHex, flexShrink: 0 }} />
                  <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-primary)' }}>{l.label}</span>
                </div>
                <p style={{ fontSize: '11px', color: 'var(--text-tertiary)', margin: 0, lineHeight: '1.4' }}>{l.tooltip}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Right: detail panel */}
        <div style={{ position: 'sticky', top: '24px' }}>
          {activeLayer && <DetailPanel layer={activeLayer} navigate={navigate} />}
        </div>
      </div>
    </div>
  );
}